<template>
  <resource-list-wrapper :title="$t('resources.external_forms.name')">
    <template #default>
      <va-list
        ref="list"
        :resource="resourceName"
        :filter.sync="customFiltersValues"
        :filters="filters"
        disable-export
        disable-create
        disableBulkDelete
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template #[`filter.search`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateFiltersValues('q', val)">
          </v-text-field>
        </template>
        <va-data-table
          :fields="fields"
          class="va-table--selectable test-table"
          disable-select
          showExpand
          disableActions
          :singleExpand="false"
          showExpandAll>
          <template #[`field.url`]="{ item }">
            <a class="table-link" :href="'https://' + item.url" target="_blank">{{ item.url }}</a>
          </template>
          <template #[`field.gtmId`]="{ item }">
            <span v-if="item.gtmId">{{ item.gtmId }}</span>
            <span class="not-added" v-else>{{ $t("resources.external_forms.not_added") }}</span>
          </template>
          <template #[`field.optimizerId`]="{ item }">
            <a
              :href="'https://optimizer.improvetool.com/tests/redirect/' + item.optimizerId"
              class="table-link"
              target="_blank"
              v-if="item.optimizerId"
              >{{
                item.optimizerId.substring(0, 8) + ".." + item.optimizerId.substring(item.optimizerId.length - 12)
              }}</a
            >
            <span class="not-added" v-else>{{ $t("resources.external_forms.not_added") }}</span>
          </template>
          <template #[`field.journayaScript`]="{ item }">
            <v-tooltip top v-if="item.journayaScript">
              <template #activator="{ on }">
                <v-icon class="info-tooltip" v-on="on" color="icon">mdi-check</v-icon>
              </template>
              <p class="info-tooltip__content">{{ item.journayaScript }}</p>
            </v-tooltip>
          </template>
          <template #[`field.leadspediaId`]="{ item }">
            <v-tooltip top v-if="item.leadspediaId">
              <template #activator="{ on }">
                <v-icon class="info-tooltip" v-on="on" color="icon">mdi-check</v-icon>
              </template>
              <p class="info-tooltip__content">{{ item.leadspediaId }}</p>
            </v-tooltip>
          </template>
          <template #[`field.types`]="{ item }">
            <span v-if="getTypeArray(item).length > 0">{{ getTypeArray(item).join(", ") }}</span>
            <span v-else></span>
          </template>

          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template v-slot:expanded-item="{ item }">
            <td colspan="8" class="nested-table-cell" v-if="item.numbers.length > 0">
              <table class="nested-table">
                <thead>
                  <tr>
                    <th style="width: 40px"></th>
                    <th style="width: 100%">
                      <div class="nested-column-wrapper">
                        <div style="width: 50%">{{ $t("resources.external_forms.fields.name") }}</div>
                        <div style="width: 50%">{{ $t("resources.external_forms.fields.phone_number") }}</div>
                      </div>
                    </th>
                    <th style="width: 412px">
                      {{ $t("resources.external_forms.fields.ringba_tag") }}
                    </th>
                    <th style="width: 104px"></th>
                    <th style="width: 214px"></th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="obj in item.numbers" :key="obj.number">
                    <td></td>
                    <td>
                      <div class="nested-column-wrapper">
                        <div style="width: 50%">{{ obj.name }}</div>
                        <div style="width: 50%">{{ obj.number }}</div>
                      </div>
                    </td>
                    <td>
                      <span v-if="obj.code">{{ getFormattedCode(obj) }}</span>
                      <span v-else class="no-code">{{ $t("resources.external_forms.no_code") }}</span>
                    </td>
                    <td>
                      <v-chip v-if="obj.type === 'script'" color="element-bg-200" class="script-chip">{{
                        $t("resources.external_forms.script")
                      }}</v-chip>
                      <v-chip v-else-if="obj.type === 'worker'" color="same-white" class="wrapper-chip">{{
                        $t("resources.external_forms.wrapper")
                      }}</v-chip>
                    </td>
                    <td>
                      <a
                        :href="`https://app.ringba.com/#/dashboard/campaign/${getCode(obj.code)}`"
                        target="_blank"
                        v-if="obj.type === 'script' && obj.code"
                        class="ringba-link">
                        <v-icon>mdi-open-in-new</v-icon>
                        {{ $t("resources.external_forms.open_ringba") }}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td colspan="8" class="nested-table-cell" v-else>
              <div class="no-data">{{ $t("resources.external_forms.no_data") }}</div>
            </td>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import debounce from "lodash.debounce";
  import definedAbility from "@/ability";

  export default {
    components: {
      ResourceListWrapper,
    },
    props: {
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      activeFilterOnload: {
        type: Object,
        default: () => ({}),
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        ability: definedAbility(),
        changeDateFormat,
        isUserUS,
        customFiltersValues: this.activeFilterOnload,
        filters: [
          {
            source: "search",
            alwaysOn: true,
          },
          {
            source: "number",
            label: "details",
          },
          {
            source: "exists[gtmId]",
            label: "GTM",
          },
          {
            source: "exists[journayaScript]",
            label: "Journaya",
          },
          {
            source: "exists[leadspediaId]",
            label: "Leadspedia",
          },
          {
            source: "formType",
            label: "Type",
          },
        ],
        fields: [
          {
            source: "url",
            label: this.$t("resources.external_forms.fields.url"),
            sortable: true,
            width: "100%",
          },
          {
            source: "gtmId",
            label: this.$t("resources.external_forms.fields.gtm_id"),
            sortable: true,
            width: "128px",
          },
          {
            source: "optimizerId",
            sortable: true,
            label: this.$t("resources.external_forms.fields.optimizer_id"),
            width: "184px",
          },
          {
            source: "journayaScript",
            label: this.$t("resources.external_forms.fields.journaya"),
            sortable: true,
            width: "100px",
            cellClass: "text-center",
          },
          {
            source: "leadspediaId",
            label: this.$t("resources.external_forms.fields.leadspedia"),
            sortable: true,
            width: "104px",
            cellClass: "text-center",
          },
          {
            source: "types",
            label: this.$t("resources.external_forms.fields.type"),
            width: "110px",
          },
          {
            source: "updatedAt",
            label: this.$t("resources.external_forms.fields.updated_at"),
            sortable: true,
            cellClass: "test",
            width: "104px",
          },
        ],
      };
    },
    mounted() {
      this.$refs.list.listState.options.sortBy = ["updatedAt"];
      this.$refs.list.listState.options.sortDesc = [true];
    },
    methods: {
      getFormattedCode(item) {
        if (item.type === "script") {
          return item.code.substring(2);
        }

        return item.code;
      },
      getTypeArray(item) {
        if (!item.types) {
          return [];
        }

        return item.types.filter(type => type !== "Blank");
      },
      getCode(code) {
        return code.substring(code.lastIndexOf("/") + 1);
      },
      updateFiltersValues: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .test-table {
    ::v-deep {
      .v-data-table {
        td,
        th {
          padding: 0px 4px;
        }

        th .v-input--checkbox {
          padding: 0 4px;
        }

        .v-data-table__checkbox {
          padding: 0 4px;
        }

        th:last-child {
          .v-icon {
            width: unset !important;
            position: absolute !important;
            bottom: 20px;
          }
        }

        .v-btn--icon {
          width: unset !important;
        }
        .expand-all-button {
          i {
            color: get-color("icon");

            &.expanded {
              background: get-color("primary-20");
              color: get-color("primary");
              border-radius: 50%;
            }
          }
        }

        .v-data-table__expand-icon {
          svg {
            fill: get-color("icon");
          }
        }
      }
    }
  }

  .nested-table-cell {
    padding: 0 !important;

    .no-data {
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: get-color("element-bg-200") !important;
    }
  }

  .nested-table {
    min-width: unset;
    border: none !important;
    border-spacing: 0;
    width: 100%;

    thead {
      th {
        background-color: get-color("element-bg-200") !important;
        border-bottom: 1px solid get-color("border") !important;
        padding: 10px 4px !important;
        font-weight: 700;
        line-height: 16px;
        font-size: 12px;
        color: get-color("subtitle");
        text-align: left;
        text-transform: uppercase;
      }
    }

    tbody {
      background-color: get-color("element-bg-200") !important;

      td {
        padding: 6px 4px !important;
      }

      tr:not(:last-child) > td {
        border-bottom: 1px solid get-color("border") !important;
      }

      .no-code {
        width: 76%;
        color: get-color("subtitle");
        font-size: 14px;
      }

      .script-chip {
        border: 1px solid get-color("border") !important;

        ::v-deep {
          span {
            color: get-color("subtitle") !important;
          }
        }
      }

      .ringba-link {
        color: get-color("primary");
        font-size: 12px;
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 4px;
        text-decoration: none;

        ::v-deep {
          i {
            color: get-color("primary") !important;
            font-size: 16px;
          }
        }
      }
    }

    .nested-column-wrapper {
      display: flex;
    }
  }

  .not-added {
    @include text-body-2();
    color: get-color("subtitle");
  }

  .table-link {
    @include text-body-2();
    color: get-color("body");
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .text-center {
    text-align: center;
  }

  .wrapper-chip {
    color: get-color("same-subtitle") !important;
  }
</style>
